/* Simple localStorage-backed wishlist.
   - Stores product handles in 'vv_wishlist' (array of strings).
   - Exposes window.VvWishlist { list, has, toggle, add, remove, count, on }.
   - Wires up [data-wishlist-toggle] buttons on product cards.
   - Updates [data-wishlist-count] elements in the header.
   - Renders grid into [data-wishlist-grid] on the wishlist page. */
(function () {
  if (window.VvWishlist) return;

  var STORAGE_KEY = 'vv_wishlist';
  var listeners = [];

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) { return []; }
  }

  function write(arr) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch (e) {}
    emit();
  }

  function emit() {
    var snapshot = read();
    listeners.forEach(function (fn) { try { fn(snapshot); } catch (e) {} });
  }

  function has(handle) { return read().indexOf(handle) !== -1; }

  function add(handle) {
    if (!handle) return;
    var list = read();
    if (list.indexOf(handle) === -1) {
      list.push(handle);
      write(list);
    }
  }

  function remove(handle) {
    if (!handle) return;
    var list = read().filter(function (h) { return h !== handle; });
    write(list);
  }

  function toggle(handle) {
    if (!handle) return false;
    if (has(handle)) { remove(handle); return false; }
    add(handle); return true;
  }

  function count() { return read().length; }

  function on(fn) { if (typeof fn === 'function') listeners.push(fn); }

  // ---- UI wiring ----

  function syncButtons(root) {
    var list = read();
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      var active = handle && list.indexOf(handle) !== -1;
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function syncCounts() {
    var n = count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n > 0 ? String(n) : '';
      el.setAttribute('data-count', String(n));
    });
  }

  function bindToggles(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      if (btn.__vvBound) return;
      btn.__vvBound = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var handle = btn.getAttribute('data-product-handle');
        toggle(handle);
      });
    });
  }

  // ---- Helpers: money + cart ----

  function money(cents) {
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      });
    } catch (e) { return (cents / 100).toFixed(0); }
  }

  function esc(s) { return String(s == null ? '' : s).replace(/"/g, '&quot;').replace(/</g, '&lt;'); }

  function cartRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.replace(/\/$/, '');
  }

  function addToCart(cartItems, btn) {
    if (!cartItems || !cartItems.length) return;
    var original = btn ? btn.textContent : null;
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
    fetch(cartRoot() + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function () { return fetch(cartRoot() + '/cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: cart.item_count, cart: cart } }));
        if (btn) {
          btn.textContent = 'Added ✓'; btn.classList.add('is-added');
          setTimeout(function () { btn.disabled = false; btn.textContent = original; btn.classList.remove('is-added'); }, 1500);
        }
      })
      .catch(function () {
        if (btn) {
          btn.textContent = 'Try again';
          setTimeout(function () { btn.disabled = false; btn.textContent = original; }, 1500);
        }
      });
  }

  // ---- Wishlist renderer (drawer + page) ----

  function buildCard(p) {
    var img = p.featured_image ? ('https:' + p.featured_image.replace(/^https?:/, '')) : '';
    // Same per-product formula used across the site (avg 4.6-5.0, 4-10 reviews).
    var avg = ((p.id % 5) + 46) / 10;
    var rounded = Math.round(avg);
    var onSale = p.compare_at_price && p.compare_at_price > p.price;

    var fv = null;
    for (var i = 0; i < (p.variants || []).length; i++) { if (p.variants[i].available) { fv = p.variants[i]; break; } }
    if (!fv && p.variants && p.variants.length) fv = p.variants[0];
    var available = !!(fv && fv.available);

    var stars = '';
    for (var s = 1; s <= 5; s++) {
      stars += s <= rounded ? '<span class="vv-wl-star">★</span>' : '<span class="vv-wl-star vv-wl-star--empty">★</span>';
    }

    var priceHtml = '<span class="vv-wl-price__now">' + money(p.price) + '</span>';
    if (onSale) {
      var pct = Math.round((p.compare_at_price - p.price) * 100 / p.compare_at_price);
      priceHtml += '<span class="vv-wl-price__was">' + money(p.compare_at_price) + '</span>' +
                   '<span class="vv-wl-price__off">' + pct + '% off</span>';
    }

    var atc = available
      ? '<button type="button" class="vv-wl-card__atc" data-wl-add="' + fv.id + '">Add to cart</button>'
      : '<button type="button" class="vv-wl-card__atc" disabled>Sold out</button>';

    var card = document.createElement('div');
    card.className = 'vv-wl-card';
    card.innerHTML =
      '<a href="/products/' + p.handle + '" class="vv-wl-card__img">' +
        (img ? '<img src="' + img + '" alt="' + esc(p.title) + '" loading="lazy">' : '') +
      '</a>' +
      '<div class="vv-wl-card__body">' +
        '<a href="/products/' + p.handle + '" class="vv-wl-card__title">' + esc(p.title) + '</a>' +
        '<span class="vv-wl-card__rating" aria-label="' + avg.toFixed(1) + ' out of 5 stars">' +
          '<span class="vv-wl-stars" aria-hidden="true">' + stars + '</span>' +
          '<span class="vv-wl-card__rnum">' + avg.toFixed(1) + '</span>' +
        '</span>' +
        '<span class="vv-wl-card__price">' + priceHtml + '</span>' +
        atc +
      '</div>' +
      '<button type="button" class="vv-wl-card__remove" data-remove="' + p.handle + '" aria-label="Remove from wishlist">×</button>';
    return { el: card, price: available ? p.price : 0 };
  }

  function renderGrid(grid) {
    var handles = read();
    var empty = grid.querySelector('[data-wishlist-empty]');
    var items = grid.querySelector('[data-wishlist-items]');
    var footer = grid.querySelector('[data-wishlist-footer]');
    var subtotalEl = grid.querySelector('[data-wishlist-subtotal]');
    var titleCount = grid.querySelector('[data-wishlist-title-count]');
    if (!items) return;

    if (handles.length === 0) {
      items.innerHTML = '';
      items.hidden = true;
      if (empty) empty.hidden = false;
      if (footer) footer.hidden = true;
      if (titleCount) { titleCount.hidden = true; titleCount.textContent = ''; }
      return;
    }
    if (empty) empty.hidden = true;
    items.hidden = false;

    Promise.all(handles.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      items.innerHTML = '';
      var subtotal = 0;
      var stale = [];
      products.forEach(function (p, idx) {
        if (!p) { stale.push(handles[idx]); return; }
        var built = buildCard(p);
        subtotal += built.price;
        items.appendChild(built.el);
      });

      if (subtotalEl) subtotalEl.textContent = money(subtotal);
      var shown = items.children.length;
      if (footer) footer.hidden = shown === 0;
      if (titleCount) {
        titleCount.hidden = shown === 0;
        titleCount.textContent = shown + (shown === 1 ? ' item' : ' items');
      }
      // Drop any products that no longer resolve (unpublished/deleted).
      if (stale.length) { stale.forEach(function (h) { remove(h); }); }
    });
  }

  function bindGrids() {
    document.querySelectorAll('[data-wishlist-grid]').forEach(function (grid) {
      if (grid.__vvBound) return;
      grid.__vvBound = true;
      grid.addEventListener('click', function (e) {
        var rm = e.target.closest('[data-remove]');
        if (rm) { remove(rm.getAttribute('data-remove')); return; }

        var add = e.target.closest('[data-wl-add]');
        if (add) {
          var vid = parseInt(add.getAttribute('data-wl-add'), 10);
          if (vid) addToCart([{ id: vid, quantity: 1 }], add);
          return;
        }

        var addAll = e.target.closest('[data-wishlist-addall]');
        if (addAll) {
          var list = [];
          grid.querySelectorAll('[data-wl-add]:not(:disabled)').forEach(function (b) {
            var v = parseInt(b.getAttribute('data-wl-add'), 10);
            if (v) list.push({ id: v, quantity: 1 });
          });
          if (list.length) addToCart(list, addAll);
          return;
        }
      });
      renderGrid(grid);
      on(function () { renderGrid(grid); });
    });
  }

  // ---- Drawer open/close ----

  function openDrawer() {
    var drawer = document.getElementById('vvWishlistDrawer');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('vv-wishlist-open');
    document.body.classList.add('vv-wishlist-open');
  }

  function closeDrawer() {
    var drawer = document.getElementById('vvWishlistDrawer');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('vv-wishlist-open');
    document.body.classList.remove('vv-wishlist-open');
  }

  function bindDrawer() {
    document.querySelectorAll('[data-wishlist-open]').forEach(function (btn) {
      if (btn.__vvDrawerBound) return;
      btn.__vvDrawerBound = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openDrawer();
      });
    });
    document.querySelectorAll('[data-wishlist-close]').forEach(function (el) {
      if (el.__vvDrawerBound) return;
      el.__vvDrawerBound = true;
      el.addEventListener('click', function (e) {
        // allow anchor clicks (e.g. "Shop now") to navigate — just close first
        closeDrawer();
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    }, { once: false });
  }

  // Cross-tab sync
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) emit();
  });

  on(function () { syncButtons(); syncCounts(); });

  function boot(root) {
    bindToggles(root);
    syncButtons(root);
    syncCounts();
    bindGrids();
    bindDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  window.VvWishlist = {
    list: read, has: has, toggle: toggle, add: add, remove: remove, count: count, on: on,
    open: openDrawer, close: closeDrawer,
    _rebind: boot
  };
})();
